import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Radio,
  RadioGroup,
  Slide,
  Snackbar,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  CardActions,
  CardHeader,
  Avatar,
  ToggleButton,
  ToggleButtonGroup,
  Badge,
  Tooltip,
  Fade,
  InputAdornment,
  Menu,
  MenuItem,
  Select,
  Divider,
  Collapse,
  DialogContent,
  Zoom,
  Popover,
  Autocomplete,
  Switch,
  FormGroup,
} from '@mui/material';
import * as Yup from 'yup';
import React, { useEffect, useMemo, useState } from 'react';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';
import FilterListIcon from '@mui/icons-material/FilterList';
import BusinessIcon from '@mui/icons-material/Business';
import ImageIcon from '@mui/icons-material/Image';
import { Form, Formik } from 'formik';
import imagepreview from '../../media/upload.png';
import NoimageFound from '../../media/noimage.jpg';
import axios from '../../axios';
import './animation.css';

// Header styled component - matching all masters
const HeaderPaper = styled(Paper)(({ theme }) => ({
  background: 'linear-gradient(135deg, #134e5e 0%, #71b280 100%)',
  borderRadius: '12px',
  padding: '16px 24px',
  marginBottom: '16px',
  boxShadow: '0 4px 20px rgba(19, 78, 94, 0.3)',
}));

// Compact Card styled component
const CompactCard = styled(Card)(({ theme }) => ({
  borderRadius: '10px',
  transition: 'all 0.3s ease',
  border: '1px solid #e8ecf0',
  position: 'relative',
  overflow: 'hidden',
  '&:hover': {
    transform: 'translateY(-3px)',
    boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
    '& .card-overlay': {
      opacity: 1,
    },
  },
}));

const CardOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(1),
  background: 'rgba(0,0,0,0.45)',
  opacity: 0,
  transition: 'opacity 0.3s ease',
  backdropFilter: 'blur(3px)',
  zIndex: 2,
  pointerEvents: 'none',
  '& .MuiIconButton-root': {
    pointerEvents: 'auto',
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  color: '#fff',
  fontWeight: 700,
  textAlign: 'left',
  backgroundColor: '#1a3a4a',
  padding: '8px 12px',
  whiteSpace: 'nowrap',
  fontSize: '0.75rem',
  letterSpacing: '0.03em',
  borderBottom: '2px solid rgba(255,255,255,0.1)',
  position: 'sticky',
  top: 0,
  zIndex: 10,
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: 'rgba(0, 0, 0, 0.02)',
  },
  '&:hover': {
    backgroundColor: 'rgba(25, 118, 210, 0.04)',
    transition: 'background-color 0.2s ease',
  },
}));

const StickyTableContainer = styled(TableContainer)(({ theme }) => ({
  maxHeight: '70vh',
  '& .MuiTableHead-root': {
    position: 'sticky',
    top: 0,
    zIndex: 10,
  },
}));

const useStyles = styled((theme) => ({
  fileInput: {
    display: 'none',
  },
}));

// Fullscreen Image Preview Dialog with transparent background
const FullscreenImageDialog = ({ open, imageUrl, onClose, title }) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullScreen
      PaperProps={{
        sx: {
          backgroundColor: 'rgba(0, 0, 0, 0.92)',
          backdropFilter: 'blur(8px)',
          boxShadow: 'none',
          borderRadius: 0,
        },
      }}
    >
      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          p: 4,
          position: 'relative',
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: 20,
            right: 20,
            color: '#fff',
            bgcolor: 'rgba(255,255,255,0.15)',
            backdropFilter: 'blur(4px)',
            '&:hover': {
              bgcolor: 'rgba(255,255,255,0.25)',
            },
            zIndex: 10,
          }}
        >
          <CloseIcon sx={{ fontSize: 32 }} />
        </IconButton>

        {title && (
          <Typography
            variant="h6"
            sx={{
              color: 'rgba(255,255,255,0.8)',
              position: 'absolute',
              top: 20,
              left: 50,
              fontWeight: 600,
              zIndex: 10,
            }}
          >
            {title}
          </Typography>
        )}

        <Zoom in={open} timeout={400}>
          <Box
            component="img"
            src={imageUrl || NoimageFound}
            alt="Preview"
            sx={{
              maxWidth: '95%',
              maxHeight: '90vh',
              objectFit: 'contain',
              borderRadius: 2,
              boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
            }}
          />
        </Zoom>

        <Typography
          variant="caption"
          sx={{
            color: 'rgba(255,255,255,0.4)',
            mt: 3,
            letterSpacing: '0.1em',
          }}
        >
          Click anywhere outside the image or press ESC to close
        </Typography>
      </DialogContent>
    </Dialog>
  );
};

const INITIAL_VALUE = {
  name: '',
  logoName: '',
  logoFile: null,
  active: 'Y',
};

const OurPartners = () => {
  const [openAlert, setOpenAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const theme = useTheme();
  const classes = useStyles();
  const [uploadedImage, setUploadedImage] = useState('');
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [alertType, setAlertType] = useState('success');
  const [edit, setEdit] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [data, setData] = useState([]);
  const [open, setOpen] = useState(false);
  const [openLoader, setOpenLoader] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [deleteId, setDeleteId] = useState(0);
  const [initialValue, setInitialValue] = useState(INITIAL_VALUE);
  const [viewMode, setViewMode] = useState('grid');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fullscreen image preview state
  const [previewImageOpen, setPreviewImageOpen] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState('');
  const [previewImageTitle, setPreviewImageTitle] = useState('');

  // Filter states using Popover
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [activeFilter, setActiveFilter] = useState(null);

  // Status options for Autocomplete
  const statusOptions = [
    { label: 'All Partners', value: 'all' },
    { label: 'Active', value: 'Y' },
    { label: 'Inactive', value: 'N' },
  ];

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        name: Yup.string()
          .trim()
          .min(3, ({ min }) => `Minimum ${min} characters required`)
          .max(70, ({ max }) => `Cannot exceed more than ${max} characters`)
          .transform((value) => (value ? value.toLowerCase() : value))
          .notOneOf(
            data
              .filter((item) => item.partnerId !== initialValue.partnerId)
              .map((item) => item.name?.trim()?.toLowerCase()),
            'Name Already Exists'
          )
          .required('Enter Name'),
        active: Yup.string().oneOf(['Y', 'N'], 'Please select Yes or No').required('Please select Yes or No'),
        logoFile: Yup.mixed().when('logoName', {
          is: (val) => !val || val.trim() === '',
          then: (schema) => schema.required('Please Upload Logo'),
          otherwise: (schema) => schema.notRequired(),
        }),
      }),
    [data, initialValue]
  );

  // API Integration
  useEffect(() => {
    getAllData();
  }, []);

  const getAllData = async () => {
    setOpenLoader(true);
    try {
      const res = await axios.instance.get('/GetAllPartner');
      setData(res.data);
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
    setOpenLoader(false);
  };

  const handleOnSubmit = async (values) => {
    setIsSubmitting(true);
    try {
      setOpenLoader(true);

      const formData = new FormData();

      for (const key in values) {
        if (key !== 'logoFile') {
          formData.append(key, values[key]);
        }
      }

      const userId = sessionStorage.getItem('UserId');
      formData.append(edit ? 'modifiedBy' : 'createdBy', userId);

      if (values.logoFile instanceof File) {
        formData.append('logoFile', values.logoFile);
      }

      const url = edit ? '/UpdatePartner' : '/InsertPartner';

      await axios.instance.post(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      handleSnackBar(`${edit ? 'Updated' : 'Added'} Successfully`, edit ? 'warning' : 'success');
      getAllData();
      handleClose();
    } catch (e) {
      console.error('Upload error:', e.message);
      handleSnackBar('Something went wrong!', 'error');
    }
    setOpenLoader(false);
    setIsSubmitting(false);
  };

  const handleDelete = async () => {
    setOpenLoader(true);
    try {
      await axios.instance.delete(`/deletePartner/${deleteId}`);
      handleSnackBar('Deleted Successfully', 'warning');
      getAllData();
    } catch (error) {
      console.error('Error fetching courses:', error);
      handleSnackBar('Something went wrong!', 'error');
    }
    setOpenLoader(false);
    handleCloseDelete();
  };

  const handleFileChange = (event, setFieldValue) => {
    const file = event.target.files[0];
    setFieldValue('logoFile', file);
    if (file) {
      setUploadedImage(URL.createObjectURL(file));
      setFieldValue('logoName', null);
    }
  };

  const handleEdit = (values) => {
    setEdit(true);
    setInitialValue(values);
    handleAddOpen();
  };

  const handleSnackBar = (message, type = 'success') => {
    setAlertMessage(message);
    setAlertType(type);
    setOpenAlert(true);
  };

  const handleCloseAlert = () => {
    setOpenAlert(false);
    setAlertMessage('');
  };

  const handleClose = () => {
    setInitialValue(INITIAL_VALUE);
    setEdit(false);
    setOpen(false);
    setUploadedImage('');
  };

  const handleCloseDelete = () => {
    setOpenDelete(false);
    setDeleteId(0);
  };

  const handleAddOpen = () => {
    setOpen(true);
  };

  const handleImagePreview = (imageUrl, title) => {
    if (!imageUrl || imageUrl === NoimageFound) {
      return;
    }
    setPreviewImageUrl(imageUrl);
    setPreviewImageTitle(title || 'Partner Logo');
    setPreviewImageOpen(true);
  };

  const handleCloseImagePreview = () => {
    setPreviewImageOpen(false);
    setPreviewImageUrl('');
    setPreviewImageTitle('');
  };

  // Filter functions with Popover
  const handleFilterOpen = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const getFilteredData = () => {
    let filtered = data;

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter((item) => item.name?.toLowerCase().includes(searchLower));
    }

    if (activeFilter && activeFilter.value !== 'all') {
      filtered = filtered.filter((item) => item.active === activeFilter.value);
    }

    return filtered;
  };

  const filteredData = getFilteredData();
  const totalRows = filteredData.length;

  const clearAllFilters = () => {
    setActiveFilter(null);
    setSearchTerm('');
    handleFilterClose();
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (activeFilter && activeFilter.value !== 'all') count += 1;
    if (searchTerm) count += 1;
    return count;
  };

  // Render compact card view - no pagination, all items displayed
  const renderCardView = () => {
    return (
      <Grid container spacing={2}>
        {filteredData.map((item) => {
          const imageUrl = item.logoName ? `${axios.baseURL}OurPartners/${item.logoName}` : NoimageFound;
          return (
            <Grid item xs={6} sm={4} md={3} lg={2} key={item.partnerId}>
              <Fade in timeout={400}>
                <CompactCard>
                  {/* Image with click to preview */}
                  <Box
                    sx={{
                      height: 120,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'grey.50',
                      p: 1.5,
                      position: 'relative',
                      cursor: 'pointer',
                    }}
                    onClick={(e) => {
                      if (!e.target.closest('.MuiIconButton-root')) {
                        handleImagePreview(imageUrl, item.name);
                      }
                    }}
                  >
                    <img
                      src={imageUrl}
                      alt={item.name}
                      style={{
                        maxWidth: '100%',
                        maxHeight: '100px',
                        objectFit: 'contain',
                      }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'rgba(0,0,0,0.15)',
                        opacity: 0,
                        transition: 'opacity 0.3s ease',
                        '&:hover': {
                          opacity: 1,
                        },
                        pointerEvents: 'none',
                      }}
                    >
                      <ImageIcon sx={{ color: '#fff', fontSize: 32 }} />
                    </Box>
                  </Box>

                  {/* Status Chip */}
                  <Chip
                    label={item.active === 'Y' ? 'Active' : 'Inactive'}
                    color={item.active === 'Y' ? 'success' : 'error'}
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 6,
                      right: 6,
                      fontWeight: 600,
                      zIndex: 3,
                      fontSize: '0.55rem',
                      height: 18,
                    }}
                  />

                  {/* Overlay with actions */}
                  <CardOverlay className="card-overlay">
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(item);
                        }}
                        sx={{
                          color: '#fff',
                          bgcolor: 'rgba(255,255,255,0.2)',
                          '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenDelete(true);
                          setDeleteId(item.partnerId);
                        }}
                        sx={{
                          color: '#fff',
                          bgcolor: 'rgba(255,255,255,0.2)',
                          '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                        }}
                      >
                        <DeleteForeverIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </CardOverlay>

                  {/* Partner Name */}
                  <Box
                    sx={{
                      p: 1,
                      textAlign: 'center',
                      borderTop: '1px solid #e8ecf0',
                      bgcolor: 'background.paper',
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        fontSize: '0.7rem',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        lineHeight: 1.2,
                      }}
                    >
                      {item.name}
                    </Typography>
                  </Box>
                </CompactCard>
              </Fade>
            </Grid>
          );
        })}
      </Grid>
    );
  };

  // Render compact table view - with sticky header
  const renderTableView = () => {
    return (
      <Paper sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid #e8ecf0' }}>
        <StickyTableContainer>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <StyledTableCell align="center" sx={{ width: 40 }}>
                  #
                </StyledTableCell>
                <StyledTableCell>Partner</StyledTableCell>
                <StyledTableCell align="center" sx={{ width: 80 }}>
                  Status
                </StyledTableCell>
                <StyledTableCell align="center" sx={{ width: 90 }}>
                  Actions
                </StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredData.map((item, index) => {
                const imageUrl = item.logoName ? `${axios.baseURL}OurPartners/${item.logoName}` : NoimageFound;
                return (
                  <StyledTableRow key={item.partnerId} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                    <TableCell align="center" sx={{ fontSize: '0.75rem', fontWeight: 500, py: 1 }}>
                      {index + 1}
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: '#f5f5f5',
                            borderRadius: 1,
                            p: 0.5,
                            flexShrink: 0,
                            cursor: 'pointer',
                          }}
                          onClick={() => handleImagePreview(imageUrl, item.name)}
                        >
                          <img
                            src={imageUrl}
                            alt={item.name}
                            style={{
                              maxWidth: '100%',
                              maxHeight: '100%',
                              objectFit: 'contain',
                            }}
                          />
                        </Box>
                        <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.8rem' }}>
                          {item.name}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell align="center" sx={{ py: 1 }}>
                      <Chip
                        icon={
                          item.active === 'Y' ? (
                            <CheckCircleOutlineIcon sx={{ fontSize: 14 }} />
                          ) : (
                            <HighlightOffIcon sx={{ fontSize: 14 }} />
                          )
                        }
                        label={item.active === 'Y' ? 'Active' : 'Inactive'}
                        color={item.active === 'Y' ? 'success' : 'error'}
                        size="small"
                        sx={{ fontWeight: 600, height: 22, fontSize: '0.65rem' }}
                      />
                    </TableCell>
                    <TableCell align="center" sx={{ py: 1 }}>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(item)}
                          sx={{ color: 'primary.main', p: 0.5 }}
                        >
                          <EditIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setOpenDelete(true);
                            setDeleteId(item.partnerId);
                          }}
                          sx={{ color: 'error.main', p: 0.5 }}
                        >
                          <DeleteForeverIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </StyledTableRow>
                );
              })}
            </TableBody>
          </Table>
        </StickyTableContainer>
      </Paper>
    );
  };

  return (
    <>
      <style>{`
        /* Sticky Dialog Styles */
        .sticky-dialog .MuiDialog-paper {
          display: flex;
          flex-direction: column;
          max-height: 95vh;
        }
        .sticky-dialog .MuiDialogContent-root {
          flex: 1 1 auto;
          overflow-y: auto;
          padding: 24px;
        }
        .sticky-dialog .MuiDialogActions-root {
          flex-shrink: 0;
        }
        .sticky-dialog .MuiDialogTitle-root {
          flex-shrink: 0;
        }
      `}</style>

      <Snackbar
        open={openAlert}
        autoHideDuration={3000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        TransitionComponent={Slide}
      >
        <Alert onClose={handleCloseAlert} severity={alertType} variant="filled" sx={{ width: '100%', borderRadius: 2 }}>
          {alertMessage}
        </Alert>
      </Snackbar>

      <Container maxWidth="xl" sx={{ pt: 3, pb: 4 }}>
        {/* Header */}
        <HeaderPaper elevation={0}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems="center">
            <Stack direction="row" alignItems="center" spacing={1}>
              <BusinessIcon sx={{ color: 'white' }} />
              <Typography variant="h6" fontWeight={700} color="white" sx={{ letterSpacing: '0.02em' }}>
                Our Partners
              </Typography>
            </Stack>
            <Typography variant="caption" color="rgba(255,255,255,0.7)">
              Total: {data.length} partners
            </Typography>
          </Stack>
        </HeaderPaper>

        {/* Search & Actions Bar */}
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', md: 'center' }}
          sx={{ mb: 3 }}
        >
          <TextField
            placeholder="Search partners..."
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ flex: 1, minWidth: { md: 200 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchTerm('')}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Stack direction="row" spacing={1} alignItems="center">
            <Badge badgeContent={getActiveFilterCount()} color="primary" invisible={getActiveFilterCount() === 0}>
              <Button
                variant="outlined"
                startIcon={<FilterListIcon />}
                onClick={handleFilterOpen}
                size="small"
                sx={{ borderRadius: 2 }}
              >
                Filters
              </Button>
            </Badge>

            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(e, val) => val && setViewMode(val)}
              size="small"
              sx={{ '& .MuiToggleButton-root': { px: 1.5 } }}
            >
              <ToggleButton value="grid">
                <ViewModuleIcon fontSize="small" />
              </ToggleButton>
              <ToggleButton value="table">
                <ViewListIcon fontSize="small" />
              </ToggleButton>
            </ToggleButtonGroup>

            <Button
              variant="contained"
              startIcon={<PlaylistAddIcon />}
              onClick={handleAddOpen}
              sx={{ borderRadius: 2, boxShadow: 2 }}
            >
              Add Partner
            </Button>
          </Stack>
        </Stack>

        {/* Filter Popover */}
        <Popover
          open={Boolean(filterAnchorEl)}
          anchorEl={filterAnchorEl}
          onClose={handleFilterClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          PaperProps={{
            sx: {
              maxWidth: 280,
              width: '100%',
              p: 2,
            },
          }}
        >
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Filter Partners
          </Typography>

          <Stack spacing={2} sx={{ mt: 1 }}>
            <Autocomplete
              size="small"
              options={statusOptions}
              getOptionLabel={(option) => option.label}
              value={activeFilter}
              onChange={(e, newValue) => setActiveFilter(newValue)}
              renderInput={(params) => <TextField {...params} label="Status" size="small" />}
            />

            <Stack direction="row" spacing={1}>
              <Button variant="contained" size="small" fullWidth onClick={clearAllFilters} startIcon={<ClearIcon />}>
                Clear All
              </Button>
              <Button variant="outlined" size="small" fullWidth onClick={handleFilterClose}>
                Apply
              </Button>
            </Stack>
          </Stack>
        </Popover>

        {/* Results count */}
        {filteredData.length > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Showing {filteredData.length} partner{filteredData.length !== 1 ? 's' : ''}
          </Typography>
        )}

        {/* Content */}
        {filteredData.length === 0 ? (
          <Paper sx={{ textAlign: 'center', py: 6, borderRadius: 3 }}>
            <Typography variant="h6" color="text.secondary">
              No partners found
            </Typography>
            <Typography variant="body2" color="text.disabled">
              Try adjusting your filters or add a new partner
            </Typography>
          </Paper>
        ) : viewMode === 'grid' ? (
          renderCardView()
        ) : (
          renderTableView()
        )}
      </Container>

      {/* Fullscreen Image Preview with transparent background */}
      <FullscreenImageDialog
        open={previewImageOpen}
        imageUrl={previewImageUrl}
        onClose={handleCloseImagePreview}
        title={previewImageTitle}
      />

      {/* Add/Edit Dialog - Redesigned with Sticky Header & Footer */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullScreen={isMobile}
        fullWidth
        className="sticky-dialog"
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: '95vh',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <Formik
          initialValues={initialValue}
          validationSchema={validationSchema}
          enableReinitialize
          onSubmit={async (values, { resetForm }) => {
            await handleOnSubmit(values);
            resetForm();
          }}
        >
          {({ submitForm, values, errors, handleChange, handleBlur, setFieldValue, touched, isSubmitting }) => (
            <Form style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              {/* Sticky Header */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '16px 24px',
                  borderBottom: '1px solid #e8ecf0',
                  backgroundColor: '#fafbfc',
                  flexShrink: 0,
                  position: 'sticky',
                  top: 0,
                  zIndex: 10,
                  borderRadius: '16px 16px 0 0',
                }}
              >
                <Typography variant="h6" fontWeight={600}>
                  {edit ? 'Edit' : 'Add'} Partner
                </Typography>
                <IconButton onClick={handleClose} size="small">
                  <CloseIcon />
                </IconButton>
              </Box>

              {/* Scrollable Content */}
              <DialogContent
                sx={{
                  p: 3,
                  overflow: 'auto',
                  flex: '1 1 auto',
                  '&:first-of-type': { pt: 3 },
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Box display="flex" flexDirection="column" alignItems="center">
                      <Box
                        sx={{
                          border: `2px dashed ${errors.logoFile && touched.logoFile ? '#d32f2f' : '#e0e0e0'}`,
                          borderRadius: 2,
                          p: 1,
                          width: '100%',
                          textAlign: 'center',
                          cursor: 'pointer',
                          minHeight: 120,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          '&:hover': { borderColor: 'primary.main' },
                        }}
                      >
                        <label
                          htmlFor="fileInput"
                          style={{
                            cursor: 'pointer',
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <img
                            src={
                              values.logoName
                                ? `${axios.baseURL}OurPartners/${values.logoName}`
                                : values.logoFile !== null
                                ? uploadedImage
                                : imagepreview
                            }
                            alt="upload"
                            style={{
                              maxWidth: '100%',
                              maxHeight: '120px',
                              objectFit: 'contain',
                            }}
                          />
                          <input
                            id="fileInput"
                            type="file"
                            accept="image/jpeg, image/png, image/jpg"
                            onChange={(event) => {
                              handleFileChange(event, setFieldValue);
                            }}
                            style={{ display: 'none' }}
                          />
                        </label>
                      </Box>
                      {errors.logoFile && touched.logoFile && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                          {errors.logoFile}
                        </Typography>
                      )}
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={8}>
                    <Stack spacing={2} height="100%">
                      <TextField
                        fullWidth
                        size="small"
                        label="Partner Name"
                        type="text"
                        name="name"
                        onBlur={handleBlur}
                        error={!!errors.name && touched.name}
                        helperText={errors.name && touched.name ? errors.name : ''}
                        value={values.name}
                        onChange={handleChange}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <BusinessIcon fontSize="small" color="action" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Stack>
                  </Grid>
                </Grid>
              </DialogContent>

              {/* Sticky Footer with Switch and Buttons */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 2,
                  padding: '12px 24px',
                  borderTop: '1px solid #e8ecf0',
                  backgroundColor: '#fafbfc',
                  flexShrink: 0,
                  position: 'sticky',
                  bottom: 0,
                  zIndex: 10,
                  borderRadius: '0 0 16px 16px',
                  flexWrap: 'wrap',
                }}
              >
                {/* Left side - Status Switch */}
                <Stack direction="row" spacing={2} alignItems="center">
                  <FormGroup row>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.active === 'Y'}
                          onChange={(e) => {
                            const newValue = e.target.checked ? 'Y' : 'N';
                            handleChange({ target: { name: 'active', value: newValue } });
                          }}
                          color="success"
                          size="small"
                        />
                      }
                      label={
                        <Typography variant="body2" fontWeight={500}>
                          {values.active === 'Y' ? 'Active' : 'Inactive'}
                        </Typography>
                      }
                    />
                  </FormGroup>
                </Stack>

                {/* Right side - Action Buttons */}
                <Stack direction="row" spacing={1}>
                  <Button variant="outlined" onClick={handleClose} size="medium">
                    Cancel
                  </Button>
                  <Button
                    onClick={submitForm}
                    variant="contained"
                    disabled={isSubmitting || isSubmitting}
                    size="medium"
                  >
                    {isSubmitting ? 'Saving...' : edit ? 'Update Partner' : 'Create Partner'}
                  </Button>
                </Stack>
              </Box>
            </Form>
          )}
        </Formik>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={openDelete} onClose={handleCloseDelete} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 600 }}>Delete Partner?</DialogTitle>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button variant="outlined" onClick={handleCloseDelete}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Loader */}
      <Dialog open={openLoader} fullWidth>
        <LinearProgress sx={{ height: 4 }} />
      </Dialog>
    </>
  );
};

export default OurPartners;
